import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputBase,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Avatar,
  Chip,
  Menu,
  useTheme,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  SearchOutlined,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
} from "@mui/icons-material";
import Button from "../../components/common/Button";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { tokens } from "../../theme";
import { toast } from "react-toastify";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import AddUserForm from "./AddUserForm";
import EditUserDialog from "./EditUserDialog";
import ConfirmDeleteUserDialog from "./ConfirmDeleteUserDialog";
import DataGrid from "../../components/common/CustomeDataGrid";
import Pagination from "../../components/common/Pagination";
import EmploymentHistoryView from "./EmploymentHistoryView";
import type { User } from "../../store/slice/user-management/types";
import { useAppDispatch } from "../../store/hooks";
import {
  deleteUser,
  fetchUsersPage,
  FetchUsersParams,
} from "../../store/slice/user-management/usersThunks";
import {
  fetchContentTypes,
  fetchGroups,
  fetchPermissions,
} from "../../store/slice/rolesAndPermissions/thunks";
import apiService from "../../service/Api";
import UserCard from "./components/UserCardView";
import RoleManagementTab from "./components/RoleManagementTab";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/permissions";

const PLATFORM_TAB_GROUPS: Array<string[] | null> = [
  null, // All Users
  ["platform:client-portal"], // Memorial Pages
  ["platform:chema"], // Chema
  ["Operations Manager", "Crew", "Streaming Producer"], // Pixel Pilot
];
const EMPLOYEES_HISTORY_TAB = PLATFORM_TAB_GROUPS.length;
const ROLES_TAB = EMPLOYEES_HISTORY_TAB + 1;

export default function UserManagement() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canAddUser = hasPermission(PERMISSIONS.USER_MANAGEMENT_ADD);
  const canEditUser = hasPermission(PERMISSIONS.USER_MANAGEMENT_CHANGE);
  const canDeleteUser = hasPermission(PERMISSIONS.USER_MANAGEMENT_DELETE);
  const canManageUser = canEditUser || canDeleteUser;

  const [users, setUsers] = useState<User[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tabValue, setTabValue] = useState(0);
  const [viewMode, setViewMode] = useState<"card" | "list">("card");
  const [inputValue, setInputValue] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [userFormOpen, setUserFormOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const userRoles = [
    { title: "Client", value: "client" },
    { title: "Funeral Director", value: "funeral_director" },
    { title: "Staff", value: "staff" },
    { title: "Admin", value: "admin" },
    { title: "Crew", value: "crew" },
    { title: "Dev", value: "dev" },
    { title: "Referral", value: "referral" },
  ];
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 12,
  });

  const platformGroupFilter = PLATFORM_TAB_GROUPS[tabValue] ?? null;

  /* -------------------- FETCH USERS (server-side paginated) -------------------- */
  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const params: FetchUsersParams = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        search: appliedSearchTerm || undefined,
        role: roleFilter !== "all" ? roleFilter : undefined,
        is_active:
          statusFilter === "all" ? undefined : statusFilter === "active",
      };
      if (platformGroupFilter?.length === 1) {
        params.groups__name = platformGroupFilter[0];
      } else if (platformGroupFilter && platformGroupFilter.length > 1) {
        params.groups__name__in = platformGroupFilter.join(",");
      }

      const data = await fetchUsersPage(params);
      setUsers(data.results);
      setTotalItems(data.count);
      setError(null);
    } catch (err) {
      const errorDetail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setError(errorDetail || "Failed to load users");
      toast.error(errorDetail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    appliedSearchTerm,
    roleFilter,
    statusFilter,
    platformGroupFilter,
  ]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  useEffect(() => {
    //fetch permissions/groups/content types
    dispatch(fetchPermissions());
    dispatch(fetchGroups());
    dispatch(fetchContentTypes());
  }, [dispatch]);

  /* -------------------- SEARCH DEBOUNCE -------------------- */
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setAppliedSearchTerm(inputValue.toLowerCase());
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [inputValue]);

  const filteredUsers = users;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  /* -------------------- HANDLERS -------------------- */
  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    userId: number,
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
  };

  const findSelectedUser = () =>
    users.find((u) => u.id === selectedUserId) ?? null;

  const handleRowClick = (params: { row: User }) => {
    navigate(`/user-management/view/${params.row.id}`);
  };

  const handleEditUser = () => {
    setSelectedUser(findSelectedUser());
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteUser = () => {
    setSelectedUser(findSelectedUser());
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDeleteUser = async () => {
    const user = selectedUser;

    if (!user) return toast.error("Could not delete the user");

    await dispatch(deleteUser(user.id)).unwrap();
    toast.success("User deleted");
    setDeleteDialogOpen(false);
    setSelectedUserId(null);
    fetchPage();
  };

  const handleCancelDeleteUser = () => {
    setDeleteDialogOpen(false);
  };

  /* -------------------- CRUD -------------------- */

  const handleSaveUser = async (formData: FormData) => {
    try {
      await apiService.post("/api/authentication/users/", formData, false);
      toast.success("User created successfully");
      fetchPage();
      setUserFormOpen(false);
    } catch (error) {
      const errorDetail = (
        error as { response?: { data?: { detail?: string } } }
      )?.response?.data?.detail;
      if (
        errorDetail &&
        errorDetail.includes("'NoneType' object has no attribute 'seek'")
      ) {
        toast.success("User created successfully");
        fetchPage();
        setUserFormOpen(false);
      } else {
        toast.error("Failed to create user");
        console.error("There was an error creating the user!", error);
      }
    }
  };

  const handleSaveEditUser = async (formData: FormData) => {
    try {
      if (!selectedUser) return;
      console.log("Logging form data : ", formData);
      await apiService.patch(
        `/api/authentication/users/${selectedUser.id}/`,
        formData,
        false,
      );
      fetchPage();
      setEditDialogOpen(false);
      toast.success("User updated successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (err) {
      let errorMessage = "Failed to update user. Please try again.";
      const errorDetail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      if (errorDetail) {
        errorMessage = errorDetail;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Update user error:", err);
    }
  };

  const backgroundColor = "#A9B2C3";

  /* -------------------- COLUMNS -------------------- */
  const columns: GridColDef<User>[] = [
    {
      field: "full_name",
      headerName: "Full Name",
      flex: 1,
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          sx={{ padding: 1, borderRadius: 2 }}
        >
          <Avatar
            sx={{ width: 40, height: 40 }}
            src={params.row.profile_picture_url || undefined}
          >
            {!params.row.profile_picture_url && (
              <PersonIcon sx={{ fontSize: 35, color: "grey.100" }} />
            )}
          </Avatar>
          <Typography>
            {params.row.first_name} {params.row.last_name}
          </Typography>
        </Box>
      ),
    },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "phone_number", headerName: "Phone", flex: 1 },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      renderCell: (params) => {
        const role = String(params.row.role || "").toLowerCase();
        const roleColorMap: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"> = {
          admin: "primary",
          staff: "info",
          crew: "success",
          client: "default",
          funeral_director: "secondary",
          dev: "warning",
          referral: "warning",
        };
        const chipColor = roleColorMap[role] || "default";
        // display a nicer label
        const label = role === "funeral_director" ? "Funeral Director" : role.replace(/_/g, " ");
        return <Chip label={label} size="small" color={chipColor} />;
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? "Active" : "Suspended"}
          color={params.row.is_active ? "success" : "warning"}
        />
      ),
    },
    {
      field: "2fa",
      headerName: "2FA",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.row.is_2fa_enabled ? "Allowed" : "Disabled"}
          color={params.row.is_2fa_enabled ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) =>
        canManageUser ? (
          <IconButton
            size="small"
            onClick={(e) => handleMenuClick(e, params.row.id)}
          >
            <MoreVertIcon />
          </IconButton>
        ) : null,
    },
  ];

  if (error && !users.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="All Users" />
          <Tab label="Memorial Pages" />
          <Tab label="Chema" />
          <Tab label="Pixel Pilot" />
          <Tab label="Employees History" />
          {canDeleteUser && <Tab label="Roles & Permissions" value={ROLES_TAB} />}
        </Tabs>

        {tabValue < EMPLOYEES_HISTORY_TAB && (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap="16px"
            >
              <Box display="flex" gap="4px" alignItems="center">
                <Box
                  sx={{
                    borderRadius: 3,
                    borderColor: "#9c6644",
                    backgroundColor: colors.primary[900],
                    display: "flex",
                    alignItems: "center",
                    pl: 1,
                    width: 220,
                  }}
                >
                  <SearchOutlined />
                  <InputBase
                    placeholder="Search by name or email..."
                    sx={{
                      ml: 1,
                      flex: 1,
                      color: backgroundColor,
                      padding: 0.5,
                    }}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                </Box>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={roleFilter}
                    onChange={(e) => handleRoleFilterChange(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {userRoles.map((role) => {
                      return (
                        <MenuItem key={role.value} value={role.value}>
                          {role.title}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </Select>
                </FormControl>

                <ToggleButtonGroup
                  size="small"
                  value={viewMode}
                  exclusive
                  onChange={(_e, value) => value && setViewMode(value)}
                >
                  <ToggleButton value="card" aria-label="Card view">
                    <ViewModuleIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="list" aria-label="List view">
                    <ViewListIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {canAddUser && (
                <Button
                  size="small"
                  variant="contained"
                  sx={{
                    padding: 1,
                  }}
                  onClick={() => setUserFormOpen(true)}
                >
                  Add New User
                </Button>
              )}
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : viewMode === "card" ? (
              <>
                {filteredUsers.length === 0 ? (
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mt: 4 }}
                  >
                    No users found.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                        lg: "repeat(4, 1fr)",
                      },
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    {filteredUsers.map((user) => (
                      <UserCard
                        key={user.id}
                        user={user}
                        colors={colors}
                        onView={() =>
                          navigate(`/user-management/view/${user.id}`)
                        }
                        onMenuClick={
                          canManageUser
                            ? (e) => handleMenuClick(e, user.id)
                            : undefined
                        }
                      />
                    ))}
                  </Box>
                )}

                {totalItems >= 10 && (
                  <Pagination
                    currentPage={paginationModel.page}
                    pageSize={paginationModel.pageSize}
                    totalCount={totalItems}
                    onPageChange={(page) =>
                      setPaginationModel((prev) => ({ ...prev, page }))
                    }
                    onPageSizeChange={(size) =>
                      setPaginationModel({ page: 0, pageSize: size })
                    }
                  />
                )}
              </>
            ) : (
              <Paper sx={{ p: 3, mt: 2 }}>
                <DataGrid
                  rows={filteredUsers}
                  columns={columns}
                  paginationMode="server"
                  rowCount={totalItems}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  onRowClick={handleRowClick}
                />
              </Paper>
            )}
          </>
        )}

        {tabValue === EMPLOYEES_HISTORY_TAB && (
          <Box sx={{ mt: 2 }}>
            <EmploymentHistoryView />
          </Box>
        )}

        {tabValue === ROLES_TAB && canDeleteUser && <RoleManagementTab />}

        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleMenuClose}>
          {canEditUser && (
            <MenuItem onClick={handleEditUser}>
              <EditIcon sx={{ mr: 1 }} /> Edit User
            </MenuItem>
          )}
          {canDeleteUser && (
            <MenuItem onClick={handleDeleteUser}>
              <DeleteIcon sx={{ mr: 1 }} /> Delete User
            </MenuItem>
          )}
        </Menu>

        {selectedUser && (
          <ConfirmDeleteUserDialog
            open={deleteDialogOpen}
            onClose={handleCancelDeleteUser}
            onConfirm={handleConfirmDeleteUser}
            userName={selectedUser.first_name + " " + selectedUser.last_name}
          />
        )}

        <AddUserForm
          open={userFormOpen}
          onClose={() => setUserFormOpen(false)}
          onSave={handleSaveUser}
        />

        {selectedUser && (
          <EditUserDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            onSave={handleSaveEditUser}
            selectedUser={selectedUser}
          />
        )}
      </Box>
    </motion.div>
  );
}
